import { getWishlistHandles } from "../elements/wishlist-toggle.js";
/**
 * Conditional visibility & state.
 *
 * Nodes carrying `condition="..."` are shown/hidden based on runtime state.
 * Instead of toggling `display:none` inline (which fights author CSS), we
 * add / remove a `lmdr-hidden` class and rely on a shared stylesheet injected
 * at boot via `ensureHiddenStyle()`.
 *
 * Supported conditions:
 *   in-stock | out-of-stock | on-sale | disabled
 *   has-cart-upsells | no-cart-upsells
 *   has-related | no-related
 *   has-items-in-last-viewed | no-items-in-last-viewed
 *   has-items-in-wishlist | no-items-in-wishlist
 */

import type { Cart, Product, ProductVariant } from "../types.js";

// ---------------------------------------------------------------------------
//  Style injection
// ---------------------------------------------------------------------------

const STYLE_ID = "lmdr-hidden-style";

/**
 * Inject a `<style>` tag that hides anything carrying the `lmdr-hidden`
 * class. Safe to call multiple times.
 */
export function ensureHiddenStyle(doc: Document = document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `[class~="lmdr-hidden"] { display: none !important; }`;
  doc.head.appendChild(style);
}

// ---------------------------------------------------------------------------
//  State shape
// ---------------------------------------------------------------------------

export interface ConditionState {
  product: Product | null;
  variant: ProductVariant | null;
  cart: Cart | null;
  hasRelated: boolean;
  hasCartUpsells: boolean;
  wishlistHandles: string[];
  lastViewedHandles: string[];
}

function setHidden(el: Element, hidden: boolean): void {
  el.classList.toggle("lmdr-hidden", hidden);
}

function isOnSale(variant: ProductVariant | null): boolean {
  if (!variant?.compareAtPrice) return false;
  return Number(variant.compareAtPrice.amount) > Number(variant.price.amount);
}

// ---------------------------------------------------------------------------
//  Apply
// ---------------------------------------------------------------------------

export interface ApplyConditionsOptions {
  root: ParentNode;
  state: ConditionState;
}

export function applyConditions({ root, state }: ApplyConditionsOptions): void {
  ensureHiddenStyle();

  // --------------------------------------------------------------------------
  //  Scoping : conditions variant-dependent vs conditions globales.
  //
  //  `applyConditions` est appelé à 2 niveaux :
  //    1. LOCAL — sur un `<smootify-product>` (clone ou main) après fetch
  //       Storefront. state.product + state.variant sont renseignés. Toutes
  //       les conditions sont calculées.
  //    2. DOCUMENT-WIDE — par `attachConditionAutoRefresh::reapply()` sur
  //       les events globaux (cart_updated / wishlist_updated / related_count
  //       / ...). state.product et state.variant sont NULL car ce scope
  //       concerne le state de listes / panier, pas un produit précis.
  //
  //  Bug critique à éviter : lors d'un reapply document-wide, on ne doit
  //  PAS toucher aux conditions scoped-to-variant (in-stock / out-of-stock /
  //  on-sale / disabled). Si on le fait avec variant=null, toutes les
  //  cards related / last-viewed / wishlist clignotent "Non disponible" au
  //  moindre clic wishlist (puisque variant.availableForSale devient
  //  undefined pour toutes les cards d'un coup).
  //
  //  Heuristique : si state n'a ni product ni variant, on considère que
  //  c'est un reapply document-wide et on laisse les conditions locales
  //  tranquilles — elles ont été posées par leur loadProduct respectif.
  // --------------------------------------------------------------------------
  const isGlobalReapply = state.product === null && state.variant === null;

  const nodes = root.querySelectorAll<HTMLElement>("[condition]");

  nodes.forEach((el) => {
    const kind = el.getAttribute("condition") ?? "";
    // Marque le node comme "résolu" — lève le masquage anti-FOUC du critical
    // CSS, quelle que soit la décision prise ci-dessous (visible / lmdr-hidden).
    // IMPORTANT : on le pose meme pour les scoped-to-variant en global reapply,
    // sinon on casserait les cards Webflow collection (/collection/xxx) qui ont
    // `condition="in-stock"` sur le smootify-product root — sans cette marque,
    // critical.css les masquerait integralement (display:none) avant meme que
    // loadProduct puisse tourner.
    el.setAttribute("data-lmdr-cond", "1");
    switch (kind) {
      case "in-stock":
        if (isGlobalReapply) {
          // Pas de variant pour evaluer. Si l element est une LEAF (span/text
          // label), cacher par defaut pour eviter qu il s affiche comme "En
          // stock" sans data. Les cards CONTAINER (smootify-product, div
          // wrapper) restent visibles — leur applyConditions local fera la
          // decision quand selectedVariant sera hydrate.
          const isLeaf = el.tagName !== "SMOOTIFY-PRODUCT" && el.children.length === 0;
          if (isLeaf) setHidden(el, true);
          break;
        }
        setHidden(el, !(state.variant?.availableForSale ?? false));
        break;

      case "out-of-stock":
        if (isGlobalReapply) {
          const isLeaf = el.tagName !== "SMOOTIFY-PRODUCT" && el.children.length === 0;
          if (isLeaf) setHidden(el, true);
          break;
        }
        setHidden(el, state.variant?.availableForSale ?? false);
        break;

      case "on-sale":
        if (isGlobalReapply) break;
        setHidden(el, !isOnSale(state.variant));
        break;

      case "disabled":
        if (isGlobalReapply) {
          // Pas de variant pour evaluer. Cache par defaut les LEAFS pour
          // eviter qu un bouton "Combinaison non disponible" s affiche
          // brievement avant l hydratation.
          const isLeaf = el.tagName !== "SMOOTIFY-PRODUCT" && el.children.length === 0;
          if (isLeaf) setHidden(el, true);
          break;
        }
        // Semantique Smootify : "disabled" = visible UNIQUEMENT quand la
        // variante est indisponible (out of stock OU combinaison invalide).
        // Equivalent fonctionnel de "out-of-stock" + pose attr/classe disabled
        // pour les form elements (button, input, a) qu on souhaite griser
        // visuellement plutot que cacher (rare cas d usage).
        //
        // Default : si pas de variant ou availableForSale=true → CACHE.
        //           si availableForSale=false → REVELE.
        {
          const isUnavailable = !!(state.variant && state.variant.availableForSale === false);
          setHidden(el, !isUnavailable);
          if (isUnavailable) {
            el.setAttribute("disabled", "");
            el.classList.add("is-disabled");
          } else {
            el.removeAttribute("disabled");
            el.classList.remove("is-disabled");
          }
        }
        break;

      case "has-cart-upsells":
        setHidden(el, !state.hasCartUpsells);
        break;

      case "no-cart-upsells":
        setHidden(el, state.hasCartUpsells);
        break;

      case "has-related":
        setHidden(el, !state.hasRelated);
        break;

      case "no-related":
        setHidden(el, state.hasRelated);
        break;

      case "has-items-in-last-viewed":
        setHidden(el, state.lastViewedHandles.length === 0);
        break;

      case "no-items-in-last-viewed":
        setHidden(el, state.lastViewedHandles.length > 0);
        break;

      case "has-items-in-wishlist":
        setHidden(el, state.wishlistHandles.length === 0);
        break;

      case "no-items-in-wishlist":
        setHidden(el, state.wishlistHandles.length > 0);
        break;

      case "is-empty":
        setHidden(el, !!(state.cart && state.cart.lines.length > 0));
        break;

      case "is-not-empty":
        setHidden(el, !state.cart || state.cart.lines.length === 0);
        break;

      default:
        break;
    }
  });
}

/**
 * État global qui pilote les conditions has-related / has-items-in-last-viewed
 * / has-cart-upsells / has-items-in-wishlist. Mis à jour par `loadMany` dans
 * smootify-product quand les listes sont chargées (event `smootify:related_count`,
 * `lmdr:wishlist_updated`, etc.).
 *
 * On garde un state mutable global plutôt que d'aller scanner le DOM à chaque
 * apply — plus robuste aux re-render Webflow.
 *
 * Note consentement : la wishlist sans login est un stockage fonctionnel
 * déclenché explicitement par le clic utilisateur. Elle doit donc continuer
 * à fonctionner même si l'utilisateur refuse les cookies analytics/marketing.
 */
const globalListState: {
  hasRelated: boolean;
  hasCartUpsells: boolean;
  lastViewedHandles: string[];
  wishlistHandles: string[];
} = {
  hasRelated: false,
  hasCartUpsells: false,
  lastViewedHandles: [],
  wishlistHandles: [],
};

// ---------------------------------------------------------------------------
//  localStorage readers (dupliqués ici pour éviter une dépendance circulaire
//  avec elements/wishlist-toggle.ts et elements/last-viewed.ts).
// ---------------------------------------------------------------------------

const LAST_VIEWED_KEY = "lmdr_last_viewed";

function readStringArray(key: string): string[] {
  try {
    if (typeof window === "undefined") return [];
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string" && v.length > 0);
  } catch {
    return [];
  }
}

/**
 * Ré-applique les conditions globales document-wide quand le panier change.
 * Utilisé par le boot runtime pour réagir à smootify:cart_updated.
 */
export function attachConditionAutoRefresh(): void {
  if (typeof document === "undefined") return;
  const d = document as Document & { __lmdrConditionAutoBound?: boolean };
  if (d.__lmdrConditionAutoBound) return;
  d.__lmdrConditionAutoBound = true;

  // Seed depuis localStorage : wishlist + last-viewed sont toujours dispo sync.
  globalListState.wishlistHandles = getWishlistHandles();
  globalListState.lastViewedHandles = readStringArray(LAST_VIEWED_KEY);

  const reapply = (extra: Partial<ConditionState> = {}): void => {
    applyConditions({
      root: document,
      state: buildConditionState({
        ...extra,
        hasRelated: globalListState.hasRelated,
        hasCartUpsells: globalListState.hasCartUpsells,
        lastViewedHandles: globalListState.lastViewedHandles,
        wishlistHandles: globalListState.wishlistHandles,
      }),
    });
  };

  document.addEventListener("smootify:cart_updated", (ev) => {
    const cart = (ev as CustomEvent).detail as Cart | null;
    reapply({ cart });
  });
  document.addEventListener("lmdr:wishlist_updated", (ev) => {
    const detail = (ev as CustomEvent<{ handles?: string[] }>).detail;
    // Priorité au detail, fallback localStorage pour les events tiers.
    globalListState.wishlistHandles = Array.isArray(detail?.handles)
      ? detail!.handles.filter((h): h is string => typeof h === "string")
      : getWishlistHandles();
    reapply();
  });

  // Écoute les signaux émis par `loadMany` pour mettre à jour le state global
  // puis ré-appliquer les conditions document-wide.
  document.addEventListener("smootify:related_count", (ev) => {
    const detail = (ev as CustomEvent<{ count?: number }>).detail;
    globalListState.hasRelated = (detail?.count ?? 0) > 0;
    reapply();
  });
  document.addEventListener("smootify:cart-upsells_count", (ev) => {
    const detail = (ev as CustomEvent<{ count?: number }>).detail;
    globalListState.hasCartUpsells = (detail?.count ?? 0) > 0;
    reapply();
  });
  document.addEventListener("smootify:last-viewed_count", (ev) => {
    const detail = (ev as CustomEvent<{ count?: number }>).detail;
    // Si loadMany signale count=0 sur last-viewed, on purge le state global.
    if ((detail?.count ?? 0) === 0) {
      globalListState.lastViewedHandles = [];
    } else {
      // Ré-hydrate depuis localStorage pour avoir la liste à jour.
      globalListState.lastViewedHandles = readStringArray(LAST_VIEWED_KEY);
    }
    reapply();
  });

  // Applique une première fois IMMÉDIATEMENT pour que les conditions
  // no-items-in-wishlist / no-items-in-last-viewed soient correctes dès le
  // premier paint (pas de flash du placeholder).
  reapply();
}

/**
 * Applique les conditions document-wide une fois au boot.
 * Utile quand on veut piloter l'affichage initial (placeholder empty-state,
 * liste wishlist, etc.) avant que le moindre event ne soit dispatché.
 */
export function applyConditionsInitial(): void {
  if (typeof document === "undefined") return;
  globalListState.wishlistHandles = getWishlistHandles();
  globalListState.lastViewedHandles = readStringArray(LAST_VIEWED_KEY);
  applyConditions({
    root: document,
    state: buildConditionState({
      hasRelated: globalListState.hasRelated,
      hasCartUpsells: globalListState.hasCartUpsells,
      lastViewedHandles: globalListState.lastViewedHandles,
      wishlistHandles: globalListState.wishlistHandles,
    }),
  });
}

/**
 * Convenience: build a `ConditionState` from individual inputs with sane
 * defaults for the stub fields.
 */
export function buildConditionState(partial: Partial<ConditionState>): ConditionState {
  return {
    product: partial.product ?? null,
    variant: partial.variant ?? null,
    cart: partial.cart ?? null,
    hasRelated: partial.hasRelated ?? false,
    hasCartUpsells: partial.hasCartUpsells ?? false,
    wishlistHandles: partial.wishlistHandles ?? [],
    lastViewedHandles: partial.lastViewedHandles ?? [],
  };
}
